import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { RecadastramentoContext } from "@contexts";
import { Alert, Button, Col, ComboBox, Form, Input, Row } from "@intech/web-ui";
import { EstadoCivilEntidade, WebRecadParticipanteDadosEntidade } from "@entidades";
import { ArquivoUploadItem } from "@src/components/ArquivoUpload";
import { RecadastramentoService } from "@services";
import { ArquivoUpload, LoadingComponent } from "@components";
import { FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";
import { HouveAlteracao, StringIsNullOrEmpty } from "@helpers";

interface IProps {
  avancar: (PassoEspecifico?: number | null) => Promise<void>;
  voltar: (PassoEspecifico?: number | null) => Promise<void>;
}

export const Passo3: React.FC<IProps> = ({
  avancar,
  voltar
}) => {
  const context = useContext(RecadastramentoContext);
  const isMountedRef = useRef(true);
  const form = useRef<Form>(null);

  const ListaEstadoCivilObrigatorio = ["2"];
  const ListaEstadoCivilObrigatorioArquivo = ["2", "4"];

  const [ListaArquivo, setListaArquivo] = useState<Array<ArquivoUploadItem>>(new Array<ArquivoUploadItem>());
  const [NomeConjuge, setNomeConjuge] = useState<string>("");
  const [NomeConjugeOriginal, setNomeConjugeOriginal] = useState<string>("");
  const [CPFConjuge, setCPFConjuge] = useState<string>("");
  const [CPFConjugeOriginal, setCPFConjugeOriginal] = useState<string>("");
  const [EstadoCivil, setEstadoCivil] = useState<string>("");
  const [ListaEstadoCivil, setListaEstadoCivil] = useState<EstadoCivilEntidade[]>([]);
  const [DadosOriginais, setDadosOriginais] = useState<WebRecadParticipanteDadosEntidade>(null);

  const [Loading, setLoading] = useState<boolean>(true);
  const [ErroForm, setErroForm] = useState<string>("");
  const [Erro, setErro] = useState<string>("");

  useEffect(() => () => { isMountedRef.current = false }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!isMountedRef.current)
          return;

        const dados = await context.buscarDadosParticipante();
        const dadosOriginais = await context.buscarDadosOriginais();

        const ListaEstadoCivil = await RecadastramentoService.BuscarListaEstadoCivil();

        const ListaArquivo = await context.buscarListaArquivo();

        setListaArquivo(ListaArquivo);

        const EstadoCivil = dados.CD_ESTADO_CIVIL || dadosOriginais.CD_ESTADO_CIVIL || "";

        const MostrarDadosOriginais = ListaEstadoCivilObrigatorio.includes(EstadoCivil);

        const NomeConjuge = MostrarDadosOriginais ? dadosOriginais.NOME_CONJUGE || "" : dados.NOME_CONJUGE;
        const CPFConjuge = MostrarDadosOriginais ? dadosOriginais.CPF_CONJUGE || "" : dados.CPF_CONJUGE;

        setNomeConjuge(NomeConjuge);
        setNomeConjugeOriginal(NomeConjuge);
        setCPFConjuge(CPFConjuge);
        setCPFConjugeOriginal(CPFConjuge);
        setEstadoCivil(EstadoCivil);

        setListaEstadoCivil(ListaEstadoCivil);
        setDadosOriginais(dadosOriginais);
      } catch (e) {
        const error: any = e;
        const messsage = error.response ? error.response.data : error;
        setErro(messsage);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ObrigatoriedadeCampos = useCallback(() => {
    return ListaEstadoCivilObrigatorio.includes(EstadoCivil);
  }, [ListaEstadoCivilObrigatorio, EstadoCivil]);

  const PrecisaEnviarDocumento = useCallback(() => {
    try {
      if (!isMountedRef.current || !DadosOriginais)
        return;
      const houveAlteracao = (
        (DadosOriginais ? HouveAlteracao(DadosOriginais.CD_ESTADO_CIVIL, EstadoCivil) : false) ||
        HouveAlteracao(NomeConjugeOriginal, NomeConjuge) ||
        HouveAlteracao(CPFConjugeOriginal, CPFConjuge)
      );

      const EstadoCivilObrigatorioArquivo = ListaEstadoCivilObrigatorioArquivo.includes(EstadoCivil);
      const ListaArquivoPasso = ListaArquivo.filter((arquivo) => arquivo.Passo === "Passo3");

      return EstadoCivilObrigatorioArquivo && houveAlteracao && ListaArquivoPasso.length === 0;
    }
    catch (err) {
      setErro(err["response"] ? err["response"]["data"] : err);
    }
  }, [ListaEstadoCivilObrigatorioArquivo, DadosOriginais, EstadoCivil, NomeConjugeOriginal, NomeConjuge, CPFConjugeOriginal, CPFConjuge, ListaArquivo]);

  async function handleVoltar() {
    form?.current?.clear();
    setErroForm("");
    try {
      if (await salvarDados()) {
        voltar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function handleAvancar() {
    form?.current.clear();
    setErroForm("");
    try {
      if (await salvarDados()) {
        if (StringIsNullOrEmpty(EstadoCivil)) {
          setErroForm("Campo Estado Civil obrigatório.");
        }

        if (PrecisaEnviarDocumento()) {
          setErroForm("O envio de pelo menos um documento é obrigatório.");
          return;
        }

        avancar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function salvarDados() {
    try {
      if (form?.current?.validate()) {
        if (CPFConjuge) await RecadastramentoService.ValidarCpf(CPFConjuge);

        const dadosEstadoCivil = ListaEstadoCivil.filter(x => x.CD_ESTADO_CIVIL === EstadoCivil)[0];

        if (!dadosEstadoCivil) {
          setErroForm("Houve um erro ao carregar os dados do seu estado civil, favor contatar a Fundação.")
          return false;
        }

        const dados = await context.buscarDadosParticipante();
        dados.NOME_CONJUGE = NomeConjuge;
        dados.CPF_CONJUGE = CPFConjuge;
        dados.CD_ESTADO_CIVIL = dadosEstadoCivil.CD_ESTADO_CIVIL;
        dados.DS_ESTADO_CIVIL = dadosEstadoCivil.DS_ESTADO_CIVIL;

        context.salvarDadosParticipante(dados);
        context.salvarListaArquivo(ListaArquivo);
        return true;
      }
      return false;
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
      return false;
    }
  }

  return (
    <>
      {Loading && <LoadingComponent />}

      {!Loading && Erro && <Alert type={'danger'}>{Erro}</Alert>}

      {!Loading && !Erro && (
        <>
          <Form ref={form}>
            <ComboBox
              data={ListaEstadoCivil}
              title={"* Estado Civil"}
              value={EstadoCivil}
              onChange={setEstadoCivil}
              memberName={"DS_ESTADO_CIVIL"}
              memberValue={"CD_ESTADO_CIVIL"}
            />

            <Input
              value={NomeConjuge}
              onChange={setNomeConjuge}
              title={"Nome do Conjuge"}
              max={40}
              required={ObrigatoriedadeCampos()}
            />

            <Input
              value={CPFConjuge}
              onChange={setCPFConjuge}
              title={"CPF do Conjuge"}
              mask={"cpf"}
              required={ObrigatoriedadeCampos()}
            />

            <Alert formAlert type={"danger"} />
            <Alert type={"danger"} >{ErroForm}</Alert>

            <ArquivoUpload
              ListaArquivo={ListaArquivo}
              setListaArquivo={setListaArquivo}
              texto={"Em caso de alteração do estado civil, anexar cópia da certidão de casamento ou da União Estável."}
              passo={"Passo3"}
              controller={"Recadastramento"}
            />

            <Row>
              <Col>
                <Button
                  type={"secondary"}
                  title={"Voltar"}
                  icon={<FaAngleDoubleLeft />}
                  onClick={handleVoltar}
                  loadingText={"Voltando..."}
                  usesLoading
                  block
                  iconRight
                />
              </Col>

              <Col>
                <Button
                  type={"primary"}
                  title={"Continuar"}
                  icon={<FaAngleDoubleRight />}
                  onClick={handleAvancar}
                  loadingText={"Continuando..."}
                  usesLoading
                  block
                />
              </Col>
            </Row>
          </Form>
        </>)}
    </>
  );
}
